import React from 'react'
import { Link } from '@inertiajs/react'
import { AlertTriangle, Info, Scale } from 'lucide-react'
import { HelpTooltip } from '@/components/HelpTooltip'
import MarketIntelligenceNav from '@/components/MarketIntelligenceNav'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { MarketComparison, MarketFreshness, MarketPositionRuleStatus } from '@/types/market'

export const MARKET_GUIDE_PATH = '/guides/market-intelligence'

export const PAGE_HELP =
  "Your price for one room and two guests, night by night, against your competitors' list prices on Booking.com."

/** Smooth in-page jump; the href stays a real anchor so it works without script too. */
export function scrollToSection(event: React.MouseEvent<HTMLAnchorElement>, id: string) {
  const target = document.getElementById(id)
  if (!target) return
  event.preventDefault()
  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/** When the competitors were last checked. Amber when the check is old or someone was missed. */
export function FreshnessChip({ freshness }: { freshness: MarketFreshness }) {
  const partial = freshness.stale || freshness.missingCompetitors.length > 0
  return (
    <a
      href="#about-this-data"
      onClick={(e) => scrollToSection(e, 'about-this-data')}
      title={freshness.sentence}
      className={cn(
        'inline-flex h-8 cursor-pointer items-center gap-2 rounded-full border bg-white px-3 text-xs font-medium shadow-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        partial
          ? 'border-amber-200 text-amber-800 hover:bg-amber-50'
          : 'border-border/60 text-foreground hover:bg-muted/50'
      )}
    >
      <span className="relative flex h-2 w-2">
        {!partial && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40 motion-reduce:hidden" />
        )}
        <span
          className={cn('relative inline-flex h-2 w-2 rounded-full', partial ? 'bg-amber-500' : 'bg-emerald-500')}
        />
      </span>
      {freshness.lastCheckedLabel}
    </a>
  )
}

/** Whether this page moves any price: the Market Position rule, by name, linking to its settings. */
export function RuleChip({ rule }: { rule: MarketPositionRuleStatus }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            href={rule.href}
            className={cn(
              'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border bg-white px-3 text-xs font-medium shadow-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              rule.enabled
                ? 'border-primary/30 text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/5'
                : 'border-border/60 text-slate-600 hover:bg-muted/50'
            )}
          >
            <Scale className="h-3.5 w-3.5" />
            Market Position: {rule.label}
          </Link>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{rule.sentence}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** "Your price: Double-Studio-Ensuite HR, Base Rate, 2 guests." plus why, and where to change it. */
export function ComparisonLine({ comparison, canManage }: { comparison: MarketComparison; canManage: boolean }) {
  return (
    <div className="mt-0.5 space-y-1.5">
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
        <span>{comparison.sentence}</span>
        {comparison.detail && !comparison.choiceUnavailable && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Why this room"
                  className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">{comparison.detail}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {canManage && (
          <Link
            href={comparison.changeHref}
            className="cursor-pointer rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Change
          </Link>
        )}
      </p>
      {comparison.choiceUnavailable && comparison.detail && (
        <p className="inline-flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          {comparison.detail}
        </p>
      )}
    </div>
  )
}

/**
 * The page header: title, what is compared, the two trust chips (when were prices checked, does
 * this page move any price), the tabs, and an amber note when the latest check is old or partial.
 * The layout's warning rail is never used: that rail means a fault, and an old check is not one.
 */
export default function MarketHeader({
  propertyBasePath,
  comparison,
  freshness,
  marketPositionRule,
  canManage,
}: {
  propertyBasePath: string
  comparison: MarketComparison
  freshness: MarketFreshness | null
  marketPositionRule: MarketPositionRuleStatus
  canManage: boolean
}) {
  const partial = freshness && (freshness.stale || freshness.missingCompetitors.length > 0)

  return (
    <div className="space-y-4">
      <div
        className="animate-slide-in flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
        style={{ animationDelay: '0ms' }}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Market Intelligence</h1>
            <HelpTooltip content={PAGE_HELP} learnMore={MARKET_GUIDE_PATH} />
          </div>
          <ComparisonLine comparison={comparison} canManage={canManage} />
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:pt-0.5">
          {freshness && <FreshnessChip freshness={freshness} />}
          <RuleChip rule={marketPositionRule} />
        </div>
      </div>

      <MarketIntelligenceNav propertyBasePath={propertyBasePath} activeTab="overview" />

      {partial && (
        <div
          role="status"
          className="animate-slide-in flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800"
          style={{ animationDelay: '100ms' }}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span>{freshness.sentence}</span>
        </div>
      )}
    </div>
  )
}
